import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useMobile } from "../../hooks/useMobile";
import NtPageHead from "../../components/NtPageHead";
import NtBackButton from "./NtBackButton";
import NtSearchBar from "../../components/NtSearchBar";
import NtRootPageContainerPermissionAlert from "./NtRootPageContainerPermissionAlert";
import NtRootPageContainerScroll from "./NtRootPageContainerScroll";
import { useTheme } from "../../context/ThemeContext";

/**
 * Page wrapper rendered inside NtAppShell's floating main card.
 *
 * Layout mirrors the Direction C mockup: compact page-head at top (title +
 * subtitle + optional search/actions), then the page body (no maxWidth
 * constraint, no divider).
 */
function NtRootPageContainer({
  children,
  pageTitle,
  pageSubtitle,
  showTitleView = true,
  showBackButton = false,
  scrollEnabled = true,
  permission = true,
  searchText,
  searchFilterCount,
  setSearchText,
  onSearchFilterPress,
  actions,
  containerStyle,
  maxWidth,
}) {
  const isMobile = useMobile();
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const head = showTitleView ? (
    <View style={styles.headRow}>
      {showBackButton ? <NtBackButton containerStyle={{ marginRight: 8 }} /> : null}
      <View style={{ flex: 1, minWidth: 0 }}>
        <NtPageHead title={pageTitle} subtitle={pageSubtitle} />
      </View>
      {setSearchText ? (
        <NtSearchBar
          containerStyle={styles.search}
          filterCount={searchFilterCount}
          value={searchText}
          onChangeText={setSearchText}
          onFilterPress={onSearchFilterPress}
        />
      ) : null}
      {actions ? <View style={styles.actions}>{actions}</View> : null}
    </View>
  ) : null;

  const body = (
    <View style={[styles.body, isMobile ? styles.bodyMobile : null, maxWidth ? { maxWidth } : null]}>
      {permission ? children : <NtRootPageContainerPermissionAlert />}
    </View>
  );

  return (
    <View style={[styles.container, containerStyle]}>
      <NtRootPageContainerScroll
        enabled={scrollEnabled}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        {head}
        {body}
      </NtRootPageContainerScroll>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: t.surface, // match main card so nothing leaks through
    },
    headRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: 20,
      paddingTop: 18,
      paddingBottom: 8,
      gap: 10,
    },
    search: { minWidth: 240, maxWidth: 320 },
    actions: { flexDirection: "row", alignItems: "center", gap: 8 },
    body: {
      flex: 1,
      paddingHorizontal: 20,
      paddingTop: 8,
      paddingBottom: 20,
    },
    bodyMobile: {
      paddingHorizontal: 12,
    },
  });

export default NtRootPageContainer;
